import IconButton from '@/components/common/icon-button';
import CloseIcon from '@/components/icons/CloseIcon';
import { useScaleSize } from '@/hooks';
import { useFocusTrap } from '@/hooks/modal';
import { useModalStackStore } from '@/stores';
import clsx from 'clsx';
import React, { useCallback, useEffect, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';

interface ModalProps {
    isOpen: boolean;
    onClose: (isCountdownClose?: boolean) => void;
    title?: string;
    children: React.ReactNode;
    className?: string;
    scrollBarClassName?: string;
    wrapperClassName?: string;
    delayTimePushModal?: number;
    modalIdInit?: string;
    isLoading?: boolean;
    zIndex?: number;
    titleAriaLabel?: string;
    closeTabIndex?: number;
    closeRegionOrder?: number;
}

export const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    title,
    children,
    className = '',
    scrollBarClassName = '',
    wrapperClassName = '',
    delayTimePushModal = 0,
    modalIdInit,
    isLoading = false,
    zIndex = 60,
    titleAriaLabel,
    closeTabIndex,
    closeRegionOrder,
}) => {
    const modalRef = useRef<HTMLDivElement | null>(null);
    const generatedModalId = useId();
    const modalId = modalIdInit || generatedModalId;
    const { t } = useTranslation();
    const { scaleWidth, scaleHeight } = useScaleSize();
    const { pushModal, popModal, isModalInStack } = useModalStackStore();
    const prevActiveElementRef = useRef<HTMLElement | null>(null);

    // Reset countdown when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => {
                if (!isModalInStack(modalId)) {
                    pushModal(modalId);
                }
            }, delayTimePushModal);
        } else {
            if (isModalInStack(modalId)) {
                popModal(modalId);
            }
        }
        return () => {
            if (isModalInStack(modalId)) {
                popModal(modalId);
            }
        };
    }, [isOpen, modalId, delayTimePushModal]);

    useEffect(() => {
        if (isOpen && !prevActiveElementRef.current) {
            prevActiveElementRef.current = document.activeElement as HTMLElement;
        }
    }, [isOpen]);

    const handleClose = () => {
        const activeElement = document.querySelector('.poi-active') as HTMLElement;
        if (activeElement) {
            activeElement.focus({ preventScroll: false });
            activeElement.classList.remove('poi-active');
        } else if (prevActiveElementRef.current) {
            const elementToRestore = prevActiveElementRef.current;
            prevActiveElementRef.current = null;

            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (document.body.contains(elementToRestore) && elementToRestore.focus) {
                        try {
                            elementToRestore.focus({ preventScroll: false });
                        } catch (error) {
                            console.error('Error focusing element', error);
                        }
                    }
                });
            });
        }

        popModal(modalId);
        onClose(false);
    };

    useFocusTrap(modalRef, isOpen, handleClose, isLoading, modalId);

    if (!isOpen) {
        return null;
    }

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                overflow: 'auto',
                zIndex: zIndex,
            }}
        >
            <div className={clsx(styles.dialog, wrapperClassName)} role="dialog" aria-modal="true" aria-labelledby={title ? 'modal-title' : undefined}>
                <div ref={modalRef} className={clsx(styles['dialog__content'], className)} role="document">
                    {title && (
                        <div className={`border-border-gray-ccc contrast:border-(--bg-primary) border-b-[calc(1*var(--vh-unit))] p-[calc(36*var(--vh-unit))]`}>
                            <h2
                                id="modal-title"
                                data-modal-region
                                data-interactive
                                aria-label={titleAriaLabel ?? title}
                                className={clsx(styles['dialog__title'], 'text-text-main01 focus-border')}
                                tabIndex={0}
                            >
                                {title}
                            </h2>
                        </div>
                    )}
                    <div className={clsx('custom-scrollbar max-h-[calc(90vh-120px)] overflow-y-auto', scrollBarClassName)} tabIndex={-1}>
                        {children}
                    </div>
                    <div className="absolute right-0 bottom-[calc(-102*var(--vh-unit))] rounded-[calc(12*var(--vh-unit))] border border-(--bg-line1) bg-[#000000B2]">
                        <IconButton
                            data-modal-region
                            role="button"
                            onClick={handleClose}
                            data-playsound="ting"
                            data-interactive
                            tabIndex={closeTabIndex}
                            data-modal-region-order={closeRegionOrder}
                            ariaLabel={t('common.dialog.closePopup')}
                            icon={<CloseIcon className={styles['icon-default']} color="white" />}
                            style={{
                                width: scaleWidth(80),
                                height: scaleHeight(80),
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
